// Row-level utilities: synthetic-row detection, CSV / JSON serialization,
// and import parsing. This module is pure — no I/O — so it can be reused
// from any repository implementation or from test code.

import { csvCell, normalizeValue, type Row } from "./util";
import { ANALYSIS_COLUMNS, DB_COLUMNS, rowValue } from "./schema";

export const DRAFT_KEY = "medicx-crf-draft-v1";

/** UTF-8 BOM — prepended to every CSV so Excel (RU locale) opens it as UTF-8
 *  instead of falling back to Windows-1251 and mangling Cyrillic labels. */
const UTF8_BOM = "﻿";

/** Serialize rows to the stable CSV (semicolon-separated, UTF-8 with BOM). */
export function rowsToCsv(rows: Row[]): string {
  const lines = [DB_COLUMNS.join(";")];
  rows.forEach((row) => {
    lines.push(DB_COLUMNS.map((col) => csvCell(row[col] ?? "")).join(";"));
  });
  return UTF8_BOM + lines.join("\n");
}

/** True when a row is synthetic (flagged in storage or SYN-* coded id). */
export function isSyntheticRow(row: Row): boolean {
  if (row._synthetic === true || row._synthetic === "true") return true;
  return String(row.Patient_ID ?? "").startsWith("SYN-");
}

/**
 * Serialize the analysis-ready "clean dataset": flat columns plus the derived
 * research variables (computed on the fly), as a semicolon-separated CSV.
 *
 * Synthetic `SYN-*` rows are excluded by default — the clean dataset feeds the
 * confirmatory analysis in R/SPSS and must never mix real and synthetic data.
 * Pass `{ includeSynthetic: true }` only to export the synthetic pipeline.
 */
export function rowsToCleanCsv(
  rows: Row[],
  options: { includeSynthetic?: boolean } = {},
): string {
  const selected = options.includeSynthetic ? rows : rows.filter((row) => !isSyntheticRow(row));
  const lines = [ANALYSIS_COLUMNS.join(";")];
  selected.forEach((row) => {
    lines.push(ANALYSIS_COLUMNS.map((col) => csvCell(rowValue(row, col))).join(";"));
  });
  return UTF8_BOM + lines.join("\n");
}

/** Build the JSON backup payload (full local database snapshot). */
export function rowsToJsonPayload(rows: Row[]): string {
  const payload = { exported_at: new Date().toISOString(), columns: DB_COLUMNS, rows };
  return JSON.stringify(payload, null, 2);
}

/**
 * Parse an imported JSON backup and normalize incoming rows to the schema.
 * Accepts either a bare array or a `{ rows: [...] }` payload.
 */
export function parseImportedRows(text: string): Row[] {
  const parsed = JSON.parse(text);
  const incoming = Array.isArray(parsed) ? parsed : parsed.rows;
  if (!Array.isArray(incoming)) throw new Error("rows not found");
  return incoming.map((row: Record<string, unknown>) => {
    const normalized: Row = {};
    DB_COLUMNS.forEach((col) => {
      normalized[col] = normalizeValue(row[col] ?? "");
    });
    normalized._saved_at = (row._saved_at as string) || new Date().toISOString();
    return normalized;
  });
}
